/**
 * Confirmation Dialog Component
 * 
 * A reusable confirmation dialog with customizable content, actions, and styling.
 * Perfect for delete confirmations and other destructive actions.
 */

import React from 'react'
import { AlertTriangle, Trash2, X } from 'lucide-react'
import { Modal } from './Modal'
import { Button } from './Button'
import { cn } from '../../utils/cn'

/**
 * Props interface for ConfirmDialog
 */
interface ConfirmDialogProps {
  /** Whether the dialog is open */
  isOpen: boolean
  /** Function to close the dialog */
  onClose: () => void
  /** Function called when user confirms */
  onConfirm: () => void
  /** Dialog title */
  title: string
  /** Dialog description/message */
  description: string
  /** Optional detailed message */
  details?: string
  /** Confirm button text */
  confirmText?: string
  /** Cancel button text */
  cancelText?: string
  /** Variant for styling */
  variant?: 'danger' | 'warning' | 'info'
  /** Whether the confirm action is loading */
  isLoading?: boolean
  /** Icon to display */
  icon?: React.ReactNode
}

/**
 * ConfirmDialog Component
 * 
 * Beautiful confirmation dialog with proper styling and animations
 */
export const ConfirmDialog: React.FC<ConfirmDialogProps> = ({
  isOpen,
  onClose,
  onConfirm,
  title,
  description,
  details,
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  variant = 'danger',
  isLoading = false,
  icon
}) => {
  // ====================================
  // STYLING VARIANTS
  // ====================================

  const variantStyles = {
    danger: {
      iconBg: 'bg-red-100',
      iconColor: 'text-red-600',
      confirmButton: 'bg-red-600 hover:bg-red-700 focus:ring-red-500',
      titleColor: 'text-gray-900'
    },
    warning: {
      iconBg: 'bg-yellow-100',
      iconColor: 'text-yellow-600',
      confirmButton: 'bg-yellow-600 hover:bg-yellow-700 focus:ring-yellow-500',
      titleColor: 'text-gray-900'
    },
    info: {
      iconBg: 'bg-blue-100',
      iconColor: 'text-blue-600',
      confirmButton: 'bg-blue-600 hover:bg-blue-700 focus:ring-blue-500',
      titleColor: 'text-gray-900'
    }
  }

  const styles = variantStyles[variant]

  // Default icon based on variant
  const defaultIcon = variant === 'danger' ? <Trash2 className="w-6 h-6" /> : <AlertTriangle className="w-6 h-6" />

  // ====================================
  // EVENT HANDLERS
  // ====================================

  const handleConfirm = () => {
    onConfirm()
  }

  const handleCancel = () => {
    if (!isLoading) {
      onClose()
    }
  }

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' && !isLoading) {
      handleConfirm()
    } else if (event.key === 'Escape' && !isLoading) {
      handleCancel()
    }
  }

  // ====================================
  // RENDER
  // ====================================

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleCancel}
      size="sm"
      showCloseButton={false}
      className="max-w-md"
    >
      <div className="p-6" onKeyDown={handleKeyDown}>
        {/* Icon and Title Section */}
        <div className="flex items-start gap-4">
          {/* Icon */}
          <div className={cn(
            "flex-shrink-0 w-12 h-12 rounded-full flex items-center justify-center",
            styles.iconBg
          )}>
            <div className={styles.iconColor}>
              {icon || defaultIcon}
            </div>
          </div>

          {/* Content */}
          <div className="flex-1 min-w-0">
            <h3 className={cn(
              "text-lg font-semibold mb-2",
              styles.titleColor
            )}>
              {title}
            </h3>
            
            <p className="text-sm text-gray-500 mb-4">
              {description}
            </p>

            {details && (
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 mb-4">
                <p className="text-sm text-gray-700">
                  {details}
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex items-center justify-end gap-3 mt-6">
          <Button
            variant="outline"
            onClick={handleCancel}
            disabled={isLoading}
            className="min-w-[80px]"
          >
            {cancelText}
          </Button>
          
          <Button
            onClick={handleConfirm}
            disabled={isLoading}
            className={cn(
              "min-w-[80px] text-white focus:ring-2 focus:ring-offset-2",
              styles.confirmButton
            )}
          >
            {isLoading ? (
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                <span>Deleting...</span>
              </div>
            ) : (
              confirmText
            )}
          </Button>
        </div>
      </div>
    </Modal>
  )
}

export default ConfirmDialog